import { SurveyModel, UploadFilesEvent } from 'survey-core';
import { getPresignedUrl } from '../../api/files/get-presigned-url';
import { uploadFileToS3 } from '../../api/files/upload-file-to-s3';
import { patchSubmission } from '../../api/submissions/patch-submission';
import { UserType } from '../../types';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

export interface UploadFileHandlerProps {
  options: UploadFilesEvent;
  urlSlug: string;
  publicId?: string;
  surveyModel?: SurveyModel;
  isSigner?: boolean;
  trackingId?: string;
}

export const uploadFileHandler = async ({
  options,
  urlSlug,
  publicId,
  trackingId,
  surveyModel,
  isSigner = false,
}: UploadFileHandlerProps) => {
  try {
    const uploadPromises = options.files.map(async (file: File) => {
      let response;

      if (trackingId) {
        // Trade references flow
        response = await getPresignedUrl({
          trackingId,
          urlSlug,
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
        });
      } else if (publicId) {
        // Submission flow
        response = await getPresignedUrl({
          publicId,
          urlSlug,
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
          isSigner,
        });
      } else {
        throw new Error('Either trackingId or publicId must be provided');
      }

      if (!response || !response.success || !response.data) {
        throw new Error('Failed to get presigned URL');
      }

      const { uploadUrl, fileUrl } = response.data;

      if (!uploadUrl) {
        throw new Error('Upload URL is undefined');
      }

      await uploadToS3WithRetries(file, uploadUrl);

      return {
        file,
        content: fileUrl,
        name: file.name,
      };
    });

    const results = await Promise.all(uploadPromises);
    options.callback(results);

    // After successful upload, trigger a save (only for submission flow)
    if (publicId && surveyModel) {
      const userType = isSigner ? UserType.SIGNER : UserType.INITIAL_USER;
      await patchSubmission(urlSlug, publicId, surveyModel.data, userType);
    }
  } catch (error) {
    console.error('Error uploading files:', error);
    options.callback([], [ error instanceof Error ? error.message : 'An error occurred during file upload.' ]);
  }
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const uploadToS3WithRetries = async (file: File, uploadUrl: string): Promise<void> => {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      await uploadFileToS3(file, uploadUrl);
      return; // Success, exit the retry loop
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt < MAX_RETRIES) {
        console.log(`Upload attempt ${attempt} failed, retrying in ${RETRY_DELAY * attempt}ms...`);
        await sleep(RETRY_DELAY * attempt); // Exponential backoff
      }
    }
  }

  throw lastError;
};
